<template>
  <div class="form-data-box tab-scroll-owner" v-loader.opacity.tab="loading">
    <div class="row justify-content-md-start">
      <div class="col-md-4">
        <ul class="anchor-menu">
          <li :class="{'active': activeAnchor === 'general'}">
            <a  @click="setActiveAnchor('general')">{{ $t('patient.patientInfo') }}</a>
          </li>
          <li :class="{'active': activeAnchor === 'underlying'}">
            <a  @click="setActiveAnchor('underlying')">{{ $t('patient.underlyingDisease') }}</a>
          </li>
          <li :class="{'active': activeAnchor === 'ophthalmic'}">
            <a  @click="setActiveAnchor('ophthalmic')">{{ $t('patient.ophthalmicDiseases') }}</a>
          </li>
          <li :class="{'active': activeAnchor === 'other'}">
            <a  @click="setActiveAnchor('other')">{{ $t('patient.other') }}</a>
          </li>
        </ul>
      </div>

      <div class="col-md-8 offset-md-4 scrollbar" id="sectionsBox" ref="scrollContainer" v-if="patient">
        <div class="alert" :class="alert.class" role="alert" v-if="alert.message || alert_msg.message">
          <span v-html="alert.message"></span>
          <button type="button" class="btn-close" aria-label="Close" @click="alert.message = null"></button>
        </div>

        <section id="general">
          <div class="form-check">
            <BaseSquareCheckBox id="mydriasis" :initialChecked="localExam.mydriasis" :item="'mydriasis'" @checked="doCheck"/>
            <label class="form-check-label" for="mydriasis">
              {{ $t('exam.mydriasis') }}
            </label>
          </div>
          <div class="form-floating">
            <textarea name="name" class="form-control" ref="observation" id="observation" v-model="localExam.observation" autofocus :placeholder="$t('exam.observation')" data-test="textareaExamObservation"></textarea>
            <label for="observation">{{ $t('exam.observation') }}</label>
          </div>
          <div class="form-floating v-select-floating">
            <v-select
              id="status"
              class="v-select form"
              :placeholder="$t('exam.status')"
              v-model="statusSelected"
              data-test="vSelectExamStatus"
              :reduce="x => x.value"
              :options="statusOptions"/>
            <label for="status">{{ $t('exam.status') }}</label>
          </div>
          <div class="form-floating disabled">
            <input type="text" class="form-control" id="dateTime" :value="$filters.dateTime(exam.date)" :placeholder="$t('exam.dateAndTime')" data-test="inputExamDateTime" readonly/>
            <label for="dateTime">{{ $t('exam.dateAndTime') }}</label>
          </div>
          <div class="row" v-if="debug">
            <div class="form-floating">
              <textarea name="name" class="form-control" ref="observation" id="metadata" v-model="localExam.metadata" placeholder="METADATA" data-test="textareaExamMetadata"></textarea>
              <label for="metadata">=== METADATA ===</label>
            </div>
          </div>
        </section>

        <section v-if="!isAnonymousPatient" id="patientInfo">
          <h3 class="sub-title">{{ $t('patient.patientInfo') }}</h3>
          <div class="form-floating disabled">
            <input type="text" class="form-control" id="firstname" :value="patient.firstName" data-test="inputPatientFirstName" :placeholder="$t('patient.firstName')" readonly/>
            <label for="firstname">{{ $t('patient.firstName') }}</label>
          </div>
          <div class="form-floating disabled">
            <input type="text" class="form-control" id="surname" :value="patient.lastName" data-test="inputPatientLastName" :placeholder="$t('patient.lastName')" readonly/>
            <label for="surname">{{ $t('patient.lastName') }}</label>
          </div>
          <div class="form-floating disabled">
            <input type="text" class="form-control" id="medicalReconrdNumber" :value="patient.mrn" data-test="inputPatientMrn" :placeholder="$t('mrn')" readonly/>
            <label for="medicalReconrdNumber">{{ $t('mrn') }}</label>
          </div>
          <div class="form-floating disabled">
            <input type="text" class="form-control" id="birthday" v-model="birthday" v-maska
                   :data-maska="birthday ? $t('inputMask.date') : ''" :placeholder="$t('patient.birthday')" data-test="inputPatientBirthday" readonly/>
            <label for="birthday">{{ $t('patient.birthday') }}</label>
          </div>
        </section>

        <section id="underlying" v-if="isAnamnesisReady">
          <ExamInfoUnderlyingDiseases :underlying="underlying"/>
        </section>
        <section id="ophthalmic" v-if="isAnamnesisReady">
          <ExamInfoOphthalmicDiseases :ophthalmic="ophthalmic"/>
        </section>

        <section id="other">
          <h3 class="sub-title">{{ $t('patient.other') }}</h3>
          <div class="form-floating mb-3 disabled">
            <textarea name="name" class="form-control" id="otherObservation" :value="patient.anamnesis.otherDisease" :placeholder="$t('patient.observation')" data-test="textareaPatientObservation" readonly></textarea>
            <label for="otherObservation">{{ $t('patient.other') }}</label>
          </div>
        </section>
      </div>
    </div>

    <div class="action-bar" @wheel.prevent>
      <button class="btn btn-success" v-show="hasFormChanged" type="button" name="button" data-test="btnUpdateExam" @click="doUpdate">
        <span>{{ $t("patient.updateAction") }}</span>
      </button>

      <button class="btn btn-secondary" type="button" name="button" data-test="btnDownloadExam" @click.prevent="doDownload">
        <span>{{ $t("download") }}</span>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.action-bar {
  position:                           sticky;
  bottom:                             0;
  background:                         var(--Shades-BG);
  z-index:                            2;
}

.col-md-8 {
  // max-height:                         calc(100vh - 256px); // replaced by flex: 1 on .inside-box
  // max-height:                         calc(100vh - 324px);

  section {
    scroll-margin-top:                62px;

    &:first-child {
      padding-bottom:                 33px;
    }
  }
}

.has-alert {
  .col-md-8 {
    // max-height:                       calc(100vh - 324px); // replaced by flex: 1 on .inside-box
    // max-height:                       calc(100vh - 396px);
  }
}

@media (max-width: 1500px) {
  .col-md-8 {
    // max-height:                       calc(100vh - 186px); // replaced by flex: 1 on .inside-box
    // max-height:                       calc(100vh - 318px);
  }

  .has-alert {
    .col-md-8 {
      // max-height:                     calc(100vh - 324px); // replaced by flex: 1 on .inside-box
      // max-height:                     calc(100vh - 310px);
      // max-height:                     calc(100vh - 390px);
    }
  }
}

@media (max-width: 991px) {
  .sub-title {
    font-size:                        14px;
  }

  .col-md-8 {
    max-height:                       calc(100dvh - 295px) !important;
    padding:                          0 15px 0 24px;
  }

  .has-alert {
    .col-md-8 {
      max-height:                     calc(100dvh - 377px) !important;
      // max-height:                     calc(100dvh - 325px) !important;
      // max-height:                     calc(100vh - 366px) !important;
    }
  }

  .has-usage-bar {
    .col-md-8 {
      max-height:                     calc(100dvh - 269px) !important;
    }
  }
}

@media (max-width: 767px) {
  .has-usage-bar {
    .col-md-8 {
      max-height:                     calc(100dvh - 319px) !important;
    }
  }
}

@media screen and (min-width: 992px) and (max-width: 1365px) {
  .col-md-8 {
    // max-height:                       calc(100dvh - 250px); // replaced by flex: 1 on .inside-box
    // max-height:                       calc(100vh - 171px);
    // max-height:                       calc(100vh - 303px);
  }

  .has-alert {
    .col-md-8 {
      // max-height:                     calc(100dvh - 308px) !important; // replaced by flex: 1 on .inside-box
      // max-height:                     calc(100vh - 366px) !important;
    }
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'
import ExamInfoOphthalmicDiseases from './ExamInfoOphthalmicDiseases.vue'
import ExamInfoUnderlyingDiseases from './ExamInfoUnderlyingDiseases.vue'

// TODO EC-337 ENH deal programatically with modal control (open, close, cancel and the action)

export default {
  name: 'ExamInfoTab',
  props: {
    exam: Object,
    patient: Object,
    alert_msg: Object,
  },
  components: {
    ExamInfoOphthalmicDiseases,
    ExamInfoUnderlyingDiseases,
  },
  data: () => ({
    loading: false,
    alert: {
      message: null,
      class: null
    },
    localExam: {},
    session: null,
    disabled: true,
    statusOptions: [],
    statusSelected: 'open',
    isSpecialist: null,
    debug: false,
    initialForm: {},
    hasFormChanged: false,
    isExamMounted: false,
    birthday: '',
    activeAnchor: 'general',
    underlying: {
      diabetes: false,
      hypertension: false,
      cholesterol: false,
      smoker: false,
      nwn_underlying: false,
    },
    ophthalmic: {
      diabeticRetinopathy: false,
      dmri: false,
      glaucoma: false,
      cataract: false,
      pterygium: false,
      lowVisualAcuity: false,
      nwn_ophthalmic: false,
    },
    isAnonymousPatient: false,
    isAnamnesisReady: false,
  }),
  watch: {
    alert_msg(newVal) {
      if (newVal) {
        this.alert.message = newVal.message
        this.alert.class = newVal.class
      } else {
        this.alert.message = null
      }
    },
    exam: {
      immediate: true,
      handler(newValue) {
        this.localExam = { ...newValue }
        this.checkFormChange(newValue, 'exam')
    }, deep: true},
    localExam: {
      handler(newValue) {
        this.checkFormChange(newValue, 'exam')
    }, deep: true},
    statusSelected: {
      immediate: true,
      handler(newValue) {
        this.checkFormChange(newValue, 'statusSelected')
    }, deep: true},
  },
  mounted() {
    const userStore = useUserStore()
    this.session = userStore.sessionInfo

    this.isSpecialist = this.session.isSpecialist
    this.statusOptions = [
      {value: 'open', label: this.$i18n.t('exam.open')},
      {value: 'closed', label: this.$i18n.t('exam.closed')}
    ];

    this.isAnonymousPatient = this.patient.uuid == this.session.currentClinic.jokerPatientUuid

    this.statusSelected = this.localExam.status || this.exam.status

    const DATE_FORMAT  = this.$i18n.t('dateFormat')
    this.birthday = this.patient.birthday ? moment.utc(this.patient.birthday).format(DATE_FORMAT) : ""

    this.anamnesisFormat()

    let formWrap = {
      exam: this.localExam,
      statusSelected: this.statusSelected
    }

    this.initialForm = _.cloneDeep(formWrap)

    this.$nextTick(() => {
      this.hasFormChanged = false
    })

    this.$nextTick(() => {
      const divElement = document.getElementById('sectionsBox')
      divElement.addEventListener('scroll', this.updateActiveAnchor);
      this.updateActiveAnchor(); // Initialize the active section
    })

    this.isExamMounted = true
  },
  beforeUnmount() {
    const divElement = document.getElementById('sectionsBox')
    divElement.removeEventListener('scroll', this.updateActiveAnchor);
  },
  methods: {
    anamnesisFormat() {
      let anamnesis = this.patient.anamnesis ? this.patient.anamnesis : {}

      for (let key of Object.keys(this.ophthalmic)) {
        if (key in anamnesis)
          this.ophthalmic[key] = anamnesis[key]
      }

      for (let key of Object.keys(this.underlying)) {
        if (key in anamnesis)
          this.underlying[key] = anamnesis[key]
      }

      this.$nextTick(() => {
        this.isAnamnesisReady = true
      })
    },
    dateFormat(val) {
      return moment(val).format('L')
    },
    setActiveAnchor(anchor) {
      this.activeAnchor = anchor;
      this.$nextTick(() => {
        const headerHeight = 62;
        const element = document.getElementById(anchor);
        const container = this.$refs.scrollContainer;

        if (element && container) {
          const elementPosition = element.offsetTop;
          const offsetPosition = elementPosition - headerHeight;

          container.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      });
    },
    updateActiveAnchor() {
      const sections = ['general', 'underlying', 'ophthalmic'];
      const divElement = document.getElementById('sectionsBox')
      const divHeight = divElement.offsetHeight
      let currentActive;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (!element) continue;

        const rect = element.getBoundingClientRect();
        if (rect.top <= divHeight / 2 && rect.bottom >= divHeight / 2) {
          currentActive = section;
          break;
        }
      }

      if (currentActive && currentActive !== this.activeAnchor) {
        this.activeAnchor = currentActive;
      }
    },
    doCheck() {
      this.localExam.mydriasis = !this.localExam.mydriasis
    },
    checkFormChange(value, obj) {
      if (JSON.stringify(value) === JSON.stringify(this.initialForm[obj])) {
        this.hasFormChanged = false
      } else {
        this.hasFormChanged = true
      }
    },
    editFields() {
      this.disabled = !this.disabled;
      this.$nextTick(() => {
        this.$refs.observation.focus()
      })
    },
    async doUpdate() {
      this.alert.message = null;

      this.loading = true
      NProgress.start()
      try {
        let params = {
          exam: { ...this.localExam, status: this.statusSelected }
        }

        delete params.exam.examType
        await this.$examService.update(params)

        this.initialForm = {
          exam: _.cloneDeep(this.localExam),
          statusSelected: this.statusSelected
        }

        this.hasFormChanged = false
        this.$message.popup(this, 'general.updatedSuccess', 'operationSuccess', 'success')
      } catch(err) {
        this.$message.popup(this, 'general.updatedError', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'general.updatedError', 'danger')
      }
      NProgress.done(true)
      this.loading = false
      this.disabled = !this.disabled;
    },
    doCancel() {
      if (!this.disabled)
        this.disabled = !this.disabled;
      else {
        this.$router.push({name: 'exam-list'})
      }
    },
    doDownload() {
      this.alert.message = null;

      this.loading = true
      NProgress.start()
      try {
        this.$examService.download(this.localExam.id || this.exam.id)
      } catch(err) {
        // TODO log to bugsnag
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'request_error', 'danger')
      }
      NProgress.done(true)
      this.loading = false
    },
    activeAiClasses(exam) {
      return this.$aiService.mcRASClasses(exam)
    },
    hasMCRAS(exam) {
      return this.$aiService.hasMCRAS(exam)
    }
  }
}
</script>
